import { PauseIcon, PlayIcon, SpeakerWaveIcon } from '@heroicons/react/24/solid';
import { useRef, useState } from 'react';

/**
 * Player de áudio das respostas gravadas pelo aluno (componente do tipo Audio).
 *
 * @param src - URL do áudio da resposta
 */
export function AudioPlayer({ src }: { readonly src: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();
    else void audio.play();
  }

  function formatTime(s: number) {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  function handleProgressClick(e: React.MouseEvent<HTMLButtonElement>) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  }

  return (
    <div className="flex items-center gap-sm rounded-sm bg-ctx-layout-body p-mini">
      {/* biome-ignore lint/a11y/useMediaCaption: audio player for recorded exam answers; captions not applicable */}
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => setIsPlaying(false)}
      />

      <div className="inline-flex w-full items-center justify-center gap-nano">
        <button
          type="button"
          onClick={toggle}
          className="flex size-xs shrink-0 items-center justify-center rounded-circular bg-ctx-content-title text-ctx-layout-surface"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <PauseIcon className="size-mini" />
          ) : (
            <PlayIcon className="translate-x-px size-mini" />
          )}
        </button>

        <button
          type="button"
          className="relative h-micro flex-1 cursor-pointer overflow-hidden rounded-pill bg-ctx-layout-border"
          onClick={handleProgressClick}
          aria-label="Progresso do áudio"
        >
          <div
            className="h-full rounded-pill bg-ctx-content-title transition-none"
            style={{
              width: duration ? `${(currentTime / duration) * 100}%` : '0%',
            }}
          />
        </button>

        <span className="shrink-0 text-ctx-content-placeholder ts-caption-xxs">
          {formatTime(!isPlaying && currentTime === 0 ? duration : currentTime)}
        </span>
      </div>

      <SpeakerWaveIcon className="size-xxs shrink-0 text-ctx-content-placeholder" />
    </div>
  );
}
